<template>
  <div class="onboarding">
    <div class="left">
      <RoutineLogoIcon :is-colored="true" class="logo" />
      <div class="main" :class="{ scroll }">
        <div class="content">
          <slot name="left" />
        </div>
      </div>
      <div class="buttons">
        <BaseButton
          v-if="isPrevButtonShown"
          kind="important"
          type="tertiary"
          text="Back"
          state="default"
          class="back"
          @click="$emit('prevStep')"
        />
        <BaseButton
          kind="important"
          type="primary"
          :text="
            isNextLoading
              ? isEnd
                ? 'Launching…'
                : 'Loading…'
              : isEnd
                ? 'Launch'
                : 'Continue →'
          "
          :state="
            !isNextAllowed ? 'disabled' : isNextLoading ? 'loading' : 'default'
          "
          @click="$emit('nextStep')"
        />
      </div>
    </div>
    <div class="right">
      <div class="main">
        <slot name="right" />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, onBeforeUnmount, onMounted } from "vue";

import RoutineLogoIcon from "@/components/icons/RoutineLogoIcon.vue";

import BaseButton from "../base/BaseButton.vue";

export default defineComponent({
  components: {
    RoutineLogoIcon,
    BaseButton,
  },
  props: {
    isComingSoon: { type: Boolean, required: false, default: false },
    isEnd: { type: Boolean, required: false, default: false },
    isNextAllowed: { type: Boolean, required: true },
    isNextLoading: { type: Boolean, required: false, default: false },
    isPrevButtonShown: { type: Boolean, required: false, default: true },
    scroll: { type: Boolean, required: false, default: false },
  },
  emits: {
    nextStep: () => true,
    prevStep: () => true,
  },
  setup(props, { emit }) {
    const handleDocumentKeydown = (event: KeyboardEvent) => {
      if (event.key !== "Enter") return;
      if (!props.isNextAllowed || props.isNextLoading) return;
      event.preventDefault();
      emit("nextStep");
    };

    onMounted(() => {
      document.addEventListener("keydown", handleDocumentKeydown);
    });

    onBeforeUnmount(() => {
      document.removeEventListener("keydown", handleDocumentKeydown);
    });

    return {};
  },
});
</script>

<style scoped lang="scss">
.onboarding {
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100vh;
  .left,
  .right {
    display: flex;
    flex-direction: column;
    width: 50vw;
    max-width: 50vw;
    min-width: 50vw;
    overflow: hidden;
    box-sizing: border-box;
    .main {
      display: flex;
      flex-direction: column;
      justify-content: center;
      flex: 1;
    }
  }
  .left {
    overflow-y: auto;

    .main {
      &.scroll {
        justify-content: start;
        overflow-x: hidden;
        overflow-y: auto;
        padding: 0 0 16px 0;
      }
      .content {
        height: fit-content;
        width: 100%;
        max-width: 480px;
        margin: 0 auto;
        :deep(h1) {
          @extend %font-h3;
          margin: 0 0 6px 0;
          padding: 0;
        }
        :deep(h2) {
          @extend %font-body-regular;
          margin: 0 0 32px 0;
          padding: 0;
          @include themed() {
            color: t($grey-70);
          }
        }
        :deep(.blocks) {
          display: flex;
          flex-direction: column;
          row-gap: 10px;
          .block {
            display: flex;
            flex-direction: row;
            align-items: center;
            border-radius: 5px;
            @include transition(all 100ms ease);
            @include themed() {
              border: 1px solid t($grey-15);
            }
            .icon,
            .content {
              display: flex;
              flex-direction: column;
              justify-items: center;
            }
            svg path {
              @include transition(fill 100ms ease);
            }
            h3 {
              margin: 0;
              transition: color 100ms ease-in;
              @include themed() {
                color: t($grey-70);
              }
            }
            p {
              @extend %font-caption-regular;
              margin: 0;
              transition: color 100ms ease-in;
              @include themed() {
                color: t($grey-50);
              }
            }
            &:hover {
              @include themed() {
                background-color: t($grey-05);
                border-color: t($grey-15);
              }
            }
            &.isSelected {
              @include themed() {
                background-color: t($grey-05);
                border-color: t($grey-20);
              }
              h3 {
                @include themed() {
                  color: t($primary);
                }
              }
              p {
                @include themed() {
                  color: t($grey-50);
                }
              }
              svg path {
                @include themed() {
                  fill: t($primary);
                }
              }
            }
            &.sm {
              padding: 6px 20px;
              p {
                @extend %font-caption-regular;
                @include themed() {
                  color: t($grey-70);
                }
              }
              &.isSelected {
                p {
                  @extend %font-caption-medium;
                  @include themed() {
                    color: t($primary);
                  }
                }
              }
            }
            &.md {
              padding: 16px 24px;
              column-gap: 12px;
              h3 {
                @extend %font-caption-medium;
              }
            }
            &.lg {
              padding: 24px 32px;
              column-gap: 24px;
              h3 {
                @extend %font-h4;
              }
            }
          }
        }
        :deep(section) {
          margin-top: 32px;
          h2 {
            margin-bottom: 12px;
          }
          header {
            display: flex;
            flex-direction: row;
            column-gap: 8px;
            align-items: center;
            align-content: center;
            margin-bottom: 12px;
            h2 {
              margin: 0;
            }
          }
          .blocks {
            flex-direction: row;
            column-gap: 8px;
            flex-wrap: wrap;
          }
        }
      }
    }
    .buttons {
      flex-shrink: 0;
      display: flex;
      flex-direction: row;
      column-gap: 12px;
      padding-top: 16px;
    }
  }
  .left {
    padding: 64px 64px 32px 64px;
    flex-shrink: 1;
    @include themed() {
      border-inline-end: 1px solid t($grey-15);
    }
    .logo {
      width: 32px;
      height: 16px;
      margin-bottom: 16px;
      flex-shrink: 0;
    }
  }
  .right {
    position: relative;
    padding: 0;
    overflow: hidden;
    @include themed() {
      background-color: t($grey-03);
    }
    .main {
      position: absolute;
      top: 10%;
      inset-inline-start: 10%;
      width: max-content;
      height: max-content;
      :deep(img) {
        display: block;
        width: 50%;
        height: auto;
      }
    }
  }
}
</style>
